export type NotificationEntity = {
  id: string;
  title: string;
  icon: string;
  type: string;
};

export enum Product {
  E1 = 'e1',
  LEANKIT = 'leankit',
  PLATFORMA = 'platforma',
  PROJECTPLACE = 'projectplace',
  PROJECTPLACE_WHITEBOARD = 'projectplace_whiteboard',
  PROJECTPLACE_LOGBOOK = 'projectplace_logbook',
  TEAM_TAP = 'teamtap',
  ROADMAP = 'roadmaps',
  LAUNCHPAD = 'launchpad',
  COPILOTADMIN = 'copilot_admin',
  PPMPRO = 'ppmpro',
  E1_PRM = 'e1_prm', //portfolios strategy
  E1_WORK = 'e1_work', //portfolios work
  ADAPTIVEWORK = 'adaptivework', //adaptive work
  ADVISOR = 'advisor',
  GROUPS = 'groups',
  TEAMS = 'teams',
  ENSEMBLE = 'ensemble',
  PROJECTADVANTAGE = 'project_advantage',
  EA = 'ea',
}

/** @deprecated */
export enum Themes {
  default = 'default',
  leankit = 'leankit',
  e1 = 'e1',
  teamtap = 'teamtap',
}

export enum Project {
  OKR = 'okr',
  ROADMAP = 'roadmap',
  TEAMTAP = 'teamtap',
  NOTIFICATION = 'notification',
  PRODUCTSWITCHER = 'productswitcher',
  WHITEBOARD = 'whiteboard',
  LAUNCHPAD = 'launchpad',
  LOGBOOK = 'logbook',
  COMMENTS = 'comments',
}

export enum HostingMethod {
  STANDALONE = 'standalone',
  EMBEDDED = 'embedded',
}

export interface ProjectConstructorConfig {
  product: Product;
  theme?: Themes;
}

export type PlatformaAuthTokenClosure = () => Promise<AuthTokens>;

export type GenericComponent = {
  element: HTMLElement;
};

export interface AuthTokens {
  token?: string; // temporary, until LK returns idToken in their closure
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

export interface PlatformaJWT {
  iss: string;
  exp: number;
  iat?: number;
  planview_env_selector?: string;
  planview_admin_url?: string;
  planview_tenant_group_id?: string;
  planview_user_id?: string;
  planview_roles?: string;
}

export type ConfigProps = {
  apiToken?: string | PlatformaAuthTokenClosure;
  product: Product;
  /** @deprecated */
  theme?: Themes;
  platformaAuthTokenClosure?: PlatformaAuthTokenClosure;
  environment?: Environment;
  sessionTimeout?: number;
  /** CSP nonce to apply to styled-components-injected <style> tags */
  nonce?: string;
};

export type Environment = 'dev' | 'stg' | 'eu' | 'pd' | 'ap';

export type DefaultComponentExportProps = ConfigProps & GenericComponent;

export type DovetailStandaloneEnvironment =
  | 'dev'
  | 'staging'
  | 'prod-ap'
  | 'prod-eu'
  | 'prod-us';

export const toStandaloneEnvironment = (
  dovetailEnvironment: Environment | DovetailStandaloneEnvironment | 'us'
): DovetailStandaloneEnvironment => {
  if (['prod-us', 'pd', 'us'].includes(dovetailEnvironment)) return 'prod-us';
  if (['prod-eu', 'eu'].includes(dovetailEnvironment)) return 'prod-eu';
  if (['prod-ap', 'ap'].includes(dovetailEnvironment)) return 'prod-ap';
  if (['staging', 'stg'].includes(dovetailEnvironment)) return 'staging';
  return 'dev';
};

export const toDovetailEnvironment = (
  dovetailEnvironment: Environment | DovetailStandaloneEnvironment | 'us'
): Environment => {
  if (['prod-us', 'pd', 'us'].includes(dovetailEnvironment)) return 'pd';
  if (['prod-eu', 'eu'].includes(dovetailEnvironment)) return 'eu';
  if (['prod-ap', 'ap'].includes(dovetailEnvironment)) return 'ap';
  if (['staging', 'stg'].includes(dovetailEnvironment)) return 'stg';
  return 'dev';
};

export interface StandAloneDovetailProps extends GenericComponent {
  authTokenClosure: () => Promise<AuthTokens>;
  getUserData: ((id: string) => Promise<UserData>) &
    (() => Promise<StandAloneUserData>);
  backendApiUrl: string;
  notificationSrcUrl?: string;
  notificationApiUrl?: string;
  baseScriptUrl: string;
  environment: DovetailStandaloneEnvironment;
}

export interface CoBrand {
  url: string;
  darkUrl: string;
}

export interface Customer {
  id: string;
  name: string;
  domain: string;
  cobrand: CoBrand | undefined;
  regulatoryRegion: 'US' | 'EU' | 'AP';
  orgTags?: string[];
}

interface StandAloneCustomer {
  customerId: string;
  customerName: string;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string;
  applications: Application[];
  customer: Customer;
}

export interface Application {
  appName: string;
  description: string;
  envSelector: string;
  url: string;
  apiUrl?: string | null;
  navigateUrl: string;
  title: string;
  sandbox: boolean;
  role?: string | null;
  /**
   * @deprecated While this field is not going away, prefer to use tenantGroupId where
   * possible for data segregation. TenantGroupId is useful in the context of other Planview
   * applications trying to use your API.
   */
  tenantId?: string | null;
  tenantGroupId?: string | null;
  softwareVersion?: string | null;
  licenseName?: string | null;
}

export interface UserData {
  id: string;
  first_name: string;
  last_name: string;
  email_address: string;
  avatar: string;
}

export interface Locale {
  locale: string;
}

export interface StandAloneUserData extends UserData {
  current_application: PVApplication;
  applications: PVApplication[];
  customer: StandAloneCustomer;
}

export interface PVApplication {
  name: string;
  description: string;
  envSelector: string;
  licenseName?: string;
  url: string;
  apiUrl?: string;
  navigateUrl: string;
  title: string;
  role?: string;
  tenantGroupId?: string;
  softwareVersion?: string;
}

export type PendoEventProps = {
  name: string;
  metadata?: {
    [key: string]: string | string[] | boolean;
  };
};
export type PendoMetadata = {
  metadata: PendoEventProps['metadata'];
};

export type NotificationConfig = {
  notificationApiUrl: string;
  pubnubSubscriberKey: string;
  launchDarklyClientKey: string;
};

export type RoadmapConfig = {
  hasuraApiUrl: string;
};

export type AppConfig = {
  notification: NotificationConfig;
  roadmap?: RoadmapConfig;
};

export type ProjectConfig = {
  config: AppConfig;
};

// These should match PVAdmin (platformInfrastructure/client/src/types/index.ts)
export enum PVAdminApplication {
  ADAPTIVEWORK = 'ADAPTIVEWORK',
  ADVISOR = 'ADVISOR',
  COMMUNITY = 'COMMUNITY',
  COPILOT_ADMIN = 'COPILOT_ADMIN',
  E1_PRM = 'E1_PRM',
  EA = 'EA',
  HUB = 'HUB',
  LAUNCHPAD = 'LAUNCHPAD',
  LEANKIT = 'LEANKIT',
  LOGBOOK = 'LOGBOOK',
  LOGBOOK_REPORTING = 'LOGBOOK_REPORTING',
  OKRS = 'OKRS',
  OKRS_REPORTING = 'OKRS_REPORTING',
  PLANVIEW_ME = 'PLANVIEW_ME',
  PPMPRO = 'PPMPRO',
  PROJECTPLACE = 'PROJECTPLACE',
  ROADMAPS = 'ROADMAPS',
  SUCCESS = 'SUCCESS',
  TEAMTAP = 'TEAMTAP',
  UP = 'UP',
  VIZ = 'VIZ',
  ReleaseAndVerify = 'PLUTORA',
  PROJECTADVANTAGE = 'PROJECT_ADVANTAGE',
}
